import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  ListTodo,
  MessageSquare,
  Plus,
  RotateCw,
} from 'lucide-react';

import { listDepartments, listEmployees } from '@/api/employees.api';
import { carryOverTodo, completeTodo, createTodo, listTodos, type Todo } from '@/api/eos/todos.api';
import { TodoDetailDrawer } from '@/components/eos/TodoDetailDrawer';
import { CommentThread } from '@/components/comms/CommentThread';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Tabs } from '@/components/ui/Tabs';
import { describeEosError } from '@/lib/eos/errors';
import { toIsoDate } from '@/lib/eos/quarters';
import { hasCapability, isSuperadmin } from '@/lib/capabilities';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';

/**
 * EOS To-dos — Stream B Wave 1 (ADR-023).
 *
 * 7-day action items. Anyone can raise one and (EOS convention) assign
 * it to any active member — accountability is public. Overdue rows are
 * tinted red; carry-over pushes the due date a week while counting how
 * long the item has lingered.
 */

const SELECT_CLS =
  'rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20';

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

type TodoTab = 'mine' | 'team';

export function TodosPage() {
  const { state: authState } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();

  const me = authState.status === 'authenticated' ? authState.me : null;
  const myMembershipId = me?.membership.id ?? null;
  const canCarryAny = me !== null && (isSuperadmin(me) || hasCapability(me, 'run_meetings'));

  const [tab, setTab] = useState<TodoTab>('mine');
  const [teamDepartmentId, setTeamDepartmentId] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  const [openTodoId, setOpenTodoId] = useState<string | null>(null);
  const [discussing, setDiscussing] = useState<Todo | null>(null);

  const departmentsQuery = useQuery({ queryKey: ['departments'], queryFn: listDepartments });
  const employeesQuery = useQuery({
    queryKey: ['employees', 'directory'],
    queryFn: () => listEmployees({ status: 'active', limit: 500 }),
  });

  const nameByMembership = useMemo(() => {
    const out = new Map<string, string>();
    for (const e of employeesQuery.data?.data ?? []) {
      if (e.membershipId) out.set(e.membershipId, `${e.firstName} ${e.lastName}`);
    }
    return out;
  }, [employeesQuery.data]);

  const todosQuery = useQuery({
    queryKey: ['eos', 'todos', tab, tab === 'team' ? teamDepartmentId : 'self'],
    queryFn: () =>
      tab === 'mine'
        ? listTodos({ scope: 'mine', status: 'all' })
        : listTodos({ scope: 'team', departmentId: teamDepartmentId, status: 'all' }),
    enabled: tab === 'mine' || teamDepartmentId !== '',
  });

  const todos = useMemo(() => todosQuery.data ?? [], [todosQuery.data]);
  const openTodos = useMemo(() => todos.filter((t) => t.completedAt === null), [todos]);
  const completedTodos = useMemo(() => todos.filter((t) => t.completedAt !== null), [todos]);
  // Re-derived from the freshly fetched list so the drawer reflects mutations.
  const openTodo = useMemo(
    () => (openTodoId ? (todos.find((t) => t.id === openTodoId) ?? null) : null),
    [todos, openTodoId],
  );

  function invalidate(): void {
    void queryClient.invalidateQueries({ queryKey: ['eos', 'todos'] });
  }

  const completeMutation = useMutation({
    mutationFn: (id: string) => completeTodo(id),
    onSuccess: invalidate,
    onError: (err) => {
      toast.show(describeEosError(err, 'Could not complete the to-do.'), { variant: 'danger' });
    },
  });

  const carryMutation = useMutation({
    mutationFn: (id: string) => carryOverTodo(id),
    onSuccess: () => {
      invalidate();
      toast.show('Carried over — due date pushed a week', { variant: 'success' });
    },
    onError: (err) => {
      toast.show(describeEosError(err, 'Could not carry the to-do over.'), { variant: 'danger' });
    },
  });

  function canCarry(todo: Todo): boolean {
    return canCarryAny || (myMembershipId !== null && todo.ownerMembershipId === myMembershipId);
  }

  // Owner, creator, or superadmin — mirrors the server's complete gate
  // (wave-1 review fix: no dead checkboxes the server would 403).
  function canComplete(todo: Todo): boolean {
    return (
      (me !== null && isSuperadmin(me)) ||
      (myMembershipId !== null &&
        (todo.ownerMembershipId === myMembershipId || todo.createdByMembershipId === myMembershipId))
    );
  }

  const now = Date.now();

  function renderRow(todo: Todo) {
    const overdue = todo.completedAt === null && new Date(todo.dueDate).getTime() < now;
    const subtaskTotal = todo.subtasks?.length ?? 0;
    const subtaskDone = todo.subtasks?.filter((s) => s.completedAt !== null).length ?? 0;
    const ownerName = todo.ownerMembership
      ? `${todo.ownerMembership.user.firstName} ${todo.ownerMembership.user.lastName}`
      : todo.ownerMembershipId
        ? (nameByMembership.get(todo.ownerMembershipId) ?? 'Unknown member')
        : 'Unassigned';
    return (
      <li
        key={todo.id}
        className={`flex items-center gap-3 px-4 py-3 ${overdue ? 'bg-kw-danger-bg/40' : ''}`}
      >
        <input
          type="checkbox"
          aria-label={`Complete ${todo.title}`}
          checked={todo.completedAt !== null}
          disabled={todo.completedAt !== null || completeMutation.isPending || !canComplete(todo)}
          onChange={() => {
            completeMutation.mutate(todo.id);
          }}
          className="size-4 shrink-0 rounded border-kw-border text-kw-cerulean focus:ring-kw-cerulean/40"
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setOpenTodoId(todo.id);
              }}
              className={`truncate text-left text-[14px] hover:underline ${
                todo.completedAt ? 'text-kw-muted line-through' : 'font-medium text-kw-shark'
              }`}
            >
              {todo.title}
            </button>
            {subtaskTotal > 0 ? (
              <Badge
                variant="info"
                className="shrink-0"
                aria-label={`${String(subtaskDone)} of ${String(subtaskTotal)} subtasks done`}
              >
                ▣ {subtaskDone}/{subtaskTotal}
              </Badge>
            ) : null}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[12px] text-kw-muted">
            <span>{ownerName}</span>
            <span className={overdue ? 'font-medium text-kw-danger-text' : ''}>
              Due {todo.dueDate.slice(0, 10)}
            </span>
            {todo.carryCount > 0 ? <Badge variant="warning">Carried ×{todo.carryCount}</Badge> : null}
          </div>
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          aria-label={`Discuss ${todo.title}`}
          onClick={() => {
            setDiscussing(todo);
          }}
        >
          <MessageSquare className="size-3.5" />
        </Button>
        {todo.completedAt === null && canCarry(todo) ? (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            aria-label={`Carry over ${todo.title}`}
            onClick={() => {
              carryMutation.mutate(todo.id);
            }}
          >
            <RotateCw className="size-3.5" />
            +7d
          </Button>
        ) : null}
      </li>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="typo-h1">To-dos</h1>
          <p className="mt-1 text-[14px] text-kw-muted">
            7-day action items from L10s and day-to-day. Overdue rows turn red; carry-over pushes a week.
          </p>
        </div>
        <Button
          onClick={() => {
            setCreateOpen(true);
          }}
        >
          <Plus className="size-4" />
          Add to-do
        </Button>
      </div>

      <Tabs
        tabs={[
          { id: 'mine', label: 'Mine' },
          { id: 'team', label: 'Team' },
        ]}
        activeTab={tab}
        onChange={(id) => {
          setTab(id as TodoTab);
        }}
      />

      {tab === 'team' ? (
        <label className="flex items-center gap-2">
          <span className="text-[13px] font-medium text-kw-label">Department</span>
          <select
            value={teamDepartmentId}
            onChange={(e) => {
              setTeamDepartmentId(e.target.value);
            }}
            className={SELECT_CLS}
          >
            <option value="">Pick a department…</option>
            {(departmentsQuery.data ?? []).map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      {tab === 'team' && teamDepartmentId === '' ? (
        <Card className="p-8 text-center">
          <p className="text-[13px] text-kw-muted">Pick a department to see its team to-do list.</p>
        </Card>
      ) : todosQuery.isLoading ? (
        <div className="flex justify-center py-16">
          <div className="size-8 animate-spin rounded-full border-2 border-kw-cerulean border-t-transparent" />
        </div>
      ) : todosQuery.error ? (
        <Card className="flex items-start gap-3 border-kw-danger-text/30 bg-kw-danger-bg p-4">
          <AlertTriangle className="size-5 shrink-0 text-kw-danger-text" />
          <div>
            <p className="text-[14px] font-medium text-kw-danger-text">Couldn&apos;t load to-dos</p>
            <p className="mt-1 text-[13px] text-kw-danger-text/80">
              {todosQuery.error instanceof Error ? todosQuery.error.message : 'Unknown error'}
            </p>
          </div>
        </Card>
      ) : todos.length === 0 ? (
        <Card className="flex flex-col items-center gap-3 p-12 text-center">
          <ListTodo className="size-10 text-kw-ghost" />
          <div>
            <p className="text-[15px] font-medium text-kw-shark">No to-dos yet</p>
            <p className="mt-1 text-[13px] text-kw-muted">
              Add one — it defaults to due a week from now, the EOS rhythm.
            </p>
          </div>
          <Button
            onClick={() => {
              setCreateOpen(true);
            }}
          >
            <Plus className="size-4" />
            Add your first to-do
          </Button>
        </Card>
      ) : (
        <div className="space-y-4">
          <Card className="p-0">
            {openTodos.length === 0 ? (
              <p className="px-4 py-6 text-center text-[13px] text-kw-muted">All caught up — nothing open.</p>
            ) : (
              <ul className="divide-y divide-kw-border">{openTodos.map(renderRow)}</ul>
            )}
          </Card>

          {completedTodos.length > 0 ? (
            <Card className="p-0">
              <button
                type="button"
                onClick={() => {
                  setShowCompleted((v) => !v);
                }}
                className="flex w-full items-center gap-2 px-4 py-3 text-left text-[13px] font-medium text-kw-muted hover:text-kw-shark"
              >
                {showCompleted ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
                Completed ({completedTodos.length})
              </button>
              {showCompleted ? (
                <ul className="divide-y divide-kw-border border-t border-kw-border">
                  {completedTodos.map(renderRow)}
                </ul>
              ) : null}
            </Card>
          ) : null}
        </div>
      )}

      <CreateTodoModal
        key={`${tab}|${teamDepartmentId}`}
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
        }}
        onCreated={() => {
          invalidate();
          toast.show('To-do added', { variant: 'success' });
        }}
        defaultDepartmentId={tab === 'team' ? teamDepartmentId : ''}
      />

      {openTodo ? (
        <TodoDetailDrawer
          key={openTodo.id}
          todo={openTodo}
          onClose={() => {
            setOpenTodoId(null);
          }}
        />
      ) : null}

      {/* Discussion — comment thread on the to-do (Stream E, ADR-027). */}
      <Modal
        open={discussing !== null}
        onClose={() => {
          setDiscussing(null);
        }}
        title={discussing ? discussing.title : 'Discussion'}
      >
        {discussing ? <CommentThread entityType="todo" entityId={discussing.id} /> : null}
      </Modal>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Create to-do modal
// ─────────────────────────────────────────────────────────────────────────

function CreateTodoModal({
  open,
  onClose,
  onCreated,
  defaultDepartmentId,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  defaultDepartmentId: string;
}) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [ownerMembershipId, setOwnerMembershipId] = useState('');
  const [dueDate, setDueDate] = useState(() => toIsoDate(new Date(Date.now() + SEVEN_DAYS_MS)));

  const { state: authState } = useAuth();
  const myMembershipId = authState.status === 'authenticated' ? authState.me.membership.id : null;

  // Any member may assign to any member — EOS convention: to-dos are
  // handed out in the room, in the open. Exclude the current user by id:
  // the "Myself" option already represents self, so listing them by name
  // too made the assignee appear twice.
  const employeesQuery = useQuery({
    queryKey: ['employees', 'directory'],
    queryFn: () => listEmployees({ status: 'active', limit: 500 }),
    enabled: open,
  });
  const members = useMemo(
    () =>
      (employeesQuery.data?.data ?? []).filter(
        (e) => e.membershipId !== null && e.membershipId !== myMembershipId,
      ),
    [employeesQuery.data, myMembershipId],
  );

  const mutation = useMutation({
    mutationFn: () =>
      createTodo({
        title: title.trim(),
        description: description.trim() || undefined,
        ownerMembershipId: ownerMembershipId || undefined,
        dueDate: dueDate ? `${dueDate}T00:00:00.000Z` : undefined,
        departmentId: defaultDepartmentId || undefined,
      }),
    onSuccess: () => {
      onCreated();
      onClose();
      setTitle('');
      setDescription('');
      setOwnerMembershipId('');
      setDueDate(toIsoDate(new Date(Date.now() + SEVEN_DAYS_MS)));
    },
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (!title.trim()) return;
    mutation.mutate();
  }

  return (
    <Modal open={open} onClose={onClose} title="Add to-do" size="sm">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Title"
          name="todo-title"
          required
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
          }}
          placeholder="e.g. Send the Q3 pricing sheet"
        />

        <label className="flex flex-col gap-1">
          <span className="text-[13px] font-medium text-kw-label">Description</span>
          <textarea
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
            }}
            rows={2}
            placeholder="Optional context"
            className="rounded-kw-md border border-kw-border bg-white px-3.5 py-2.5 text-[14px] text-kw-shark placeholder:text-kw-muted focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
          />
        </label>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1">
            <span className="text-[13px] font-medium text-kw-label">Assign to</span>
            <select
              value={ownerMembershipId}
              onChange={(e) => {
                setOwnerMembershipId(e.target.value);
              }}
              className={SELECT_CLS}
            >
              <option value="">Myself</option>
              {members.map((m) => (
                <option key={m.id} value={m.membershipId ?? ''}>
                  {m.firstName} {m.lastName}
                </option>
              ))}
            </select>
          </label>

          <Input
            label="Due date"
            name="todo-due"
            type="date"
            value={dueDate}
            onChange={(e) => {
              setDueDate(e.target.value);
            }}
            hint="Defaults to a week out."
          />
        </div>

        {mutation.error ? (
          <div className="flex items-start gap-2 rounded-kw-md border border-kw-danger-text/30 bg-kw-danger-bg p-3">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-kw-danger-text" />
            <p className="text-[12px] text-kw-danger-text">
              {describeEosError(mutation.error, 'Could not add the to-do.')}
            </p>
          </div>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={mutation.isPending} disabled={!title.trim()}>
            Add to-do
          </Button>
        </div>
      </form>
    </Modal>
  );
}
